import { useState } from 'react';
import { GAMMA_LANDSCAPE_THRESHOLD_DEG } from '../../../features/quickstart/utils/forceRotate';
import { RotationLogEntry, RotationSnapshot } from './useRotationDiagnostics';
import { DeviceTilt } from './useDeviceTilt';

interface HookState {
  isPortraitOrientation: boolean;
  shouldForceLandscape: boolean;
  isVisuallyRotated: boolean;
}

interface RotationDiagnosticsPanelProps {
  snapshot: RotationSnapshot;
  hookState: HookState;
  tilt: DeviceTilt;
  log: RotationLogEntry[];
  lastLockResult: string;
  onCopyLog: () => Promise<void>;
  onClearLog: () => void;
}

const fmt = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : value.toFixed(1);

const fmtDim = (value: number | undefined): string =>
  value === undefined ? '-' : String(Math.round(value));

const suggestedFromGamma = (gamma: number | null): string => {
  if (gamma === null) return '? (no reading)';
  // Mirrors the production hysteresis in forceRotate.ts: inside the threshold
  // the app holds the previous direction rather than emitting a flat state.
  if (Math.abs(gamma) < GAMMA_LANDSCAPE_THRESHOLD_DEG)
    return `near-flat / hold previous (gamma ${gamma.toFixed(1)})`;
  return gamma >= 0 ? '+90deg (gamma>=0)' : '-90deg (gamma<0)';
};

const Row = ({ label, value }: { label: string; value: string | number | boolean }) => (
  <div className='flex justify-between gap-3'>
    <span className='text-gray-400'>{label}</span>
    <span className='font-bold text-white'>{String(value)}</span>
  </div>
);

export const RotationDiagnosticsPanel = ({
  snapshot,
  hookState,
  tilt,
  log,
  lastLockResult,
  onCopyLog,
  onClearLog,
}: RotationDiagnosticsPanelProps) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopy = async () => {
    try {
      await onCopyLog();
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    setTimeout(() => setCopyState('idle'), 1500);
  };

  const copyLabel =
    copyState === 'copied' ? 'Copied!' : copyState === 'failed' ? 'Copy failed' : 'Copy log';

  return (
    <div className='pointer-events-auto fixed left-0 top-0 z-[9999] max-h-screen w-80 max-w-[90vw] overflow-y-auto bg-black/85 p-3 font-mono text-[11px] leading-tight text-white shadow-2xl'>
      <h3 className='mb-2 font-bold text-cyan-400'>Rotation Diagnostics</h3>

      <div className='mb-2 space-y-0.5 rounded border border-cyan-800/60 bg-cyan-950/30 p-2'>
        <Row label='angle' value={snapshot.angle} />
        <Row label='orientation.type' value={snapshot.orientationType} />
        <Row label='--force-rotate-angle' value={snapshot.forceRotateAngle} />
        <Row label='legacy dir (angle-only)' value={`${snapshot.legacyAngleDirection}deg`} />
        <Row label='fullscreen' value={snapshot.isFullscreen} />
        <Row label='lock supported' value={snapshot.lockSupported} />
      </div>

      <div className='mb-2 space-y-0.5 rounded border border-purple-800/60 bg-purple-950/30 p-2'>
        <Row label='inner (w x h)' value={`${snapshot.innerWidth} x ${snapshot.innerHeight}`} />
        <Row
          label='visual (w x h)'
          value={`${fmtDim(snapshot.visualWidth)} x ${fmtDim(snapshot.visualHeight)}`}
        />
        <Row label='matchMedia portrait' value={snapshot.matchMediaPortrait} />
      </div>

      <div className='mb-2 space-y-0.5 rounded border border-green-800/60 bg-green-950/30 p-2'>
        <Row label='useIsPortraitOrientation' value={hookState.isPortraitOrientation} />
        <Row label='useShouldForceLandscape' value={hookState.shouldForceLandscape} />
        <Row label='isVisuallyRotated' value={hookState.isVisuallyRotated} />
      </div>

      <div className='mb-2 space-y-0.5 rounded border border-pink-800/60 bg-pink-950/30 p-2'>
        <Row label='tilt supported' value={tilt.supported} />
        <Row label='tilt permission' value={tilt.permission} />
        <Row label='events' value={tilt.eventCount} />
        <Row label='alpha' value={fmt(tilt.alpha)} />
        <Row label='beta' value={fmt(tilt.beta)} />
        <Row label='gamma' value={fmt(tilt.gamma)} />
        <Row label='suggested rotate' value={suggestedFromGamma(tilt.gamma)} />
      </div>

      <div className='mb-2 rounded border border-yellow-800/60 bg-yellow-950/30 p-2'>
        <Row label='last lock()' value={lastLockResult} />
      </div>

      <div className='mb-2 flex gap-2'>
        <button
          type='button'
          onClick={handleCopy}
          className='flex-1 rounded bg-cyan-700 px-2 py-1 font-semibold hover:bg-cyan-600'
        >
          {copyLabel}
        </button>
        <button
          type='button'
          onClick={onClearLog}
          className='flex-1 rounded bg-gray-700 px-2 py-1 font-semibold hover:bg-gray-600'
        >
          Clear
        </button>
      </div>

      <div className='mb-1 font-semibold text-orange-400'>Event log (last {log.length})</div>
      <div className='max-h-48 space-y-1 overflow-y-auto rounded bg-black/60 p-2'>
        {log.length === 0 && <p className='text-gray-500'>No events yet</p>}
        {log.map((entry) => (
          <div key={entry.id} className='text-[10px]'>
            <span className='text-orange-300'>[{entry.time}]</span>{' '}
            <span className='text-cyan-300'>{entry.event}</span>{' '}
            <span className='text-gray-300'>
              a={entry.snapshot.angle} t={entry.snapshot.orientationType} fr=
              {entry.snapshot.forceRotateAngle} fs={String(entry.snapshot.isFullscreen)}
            </span>
          </div>
        ))}
      </div>

      <p className='mt-2 break-all text-[9px] text-gray-500'>{navigator.userAgent}</p>
    </div>
  );
};
